/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { WMDefaultStyleName } from '@opengeoweb/webmap';
import type { TimeInterval } from '@opengeoweb/webmap';
import type { STACItem } from '../../../geotiff-layer-api/api';

export const STAC_ITEM_LOOKUP_WINDOW_MS = 60 * 60 * 1000;
export const STAC_ITEM_LOOKUP_WINDOW_INTERVAL_MULTIPLIER = 20;
export const STAC_GEOTIFF_FRAME_PRELOAD_RADIUS = 4;

export interface STACFrameItem {
  item: STACItem;
  isSelected: boolean;
}

export type STACItemLookupWindow = [string | undefined, string | undefined];

interface STACItemWithTime {
  item: STACItem;
  index: number;
  time: number;
}

export const resolvePreferredStyleName = (
  styleNames: string[],
  currentStyle?: string,
): string => {
  if (styleNames.length === 0) {
    return WMDefaultStyleName;
  }

  if (currentStyle && styleNames.includes(currentStyle)) {
    return currentStyle;
  }

  if (styleNames.includes(WMDefaultStyleName)) {
    return WMDefaultStyleName;
  }

  return styleNames[0];
};

export const getTimeIntervalInMilliseconds = (
  interval?: TimeInterval,
): number | undefined => {
  const supportedInterval =
    interval?.year === 0 && interval?.month === 0 ? interval : undefined;

  if (!supportedInterval) {
    return undefined;
  }

  const milliseconds =
    (((supportedInterval.day * 24 + supportedInterval.hour) * 60 +
      supportedInterval.minute) *
      60 +
      supportedInterval.second) *
    1000;

  return milliseconds > 0 ? milliseconds : undefined;
};

export const getStacItemLookupWindowForTimes = (
  timeValues: string[],
  windowMs = STAC_ITEM_LOOKUP_WINDOW_MS,
): STACItemLookupWindow => {
  const validTimes = timeValues
    .map((timeValue) => new Date(timeValue).getTime())
    .filter((timeValue) => Number.isFinite(timeValue));

  if (validTimes.length === 0) {
    return [undefined, undefined];
  }

  const halfWindow = windowMs / 2;
  return [
    new Date(Math.min(...validTimes) - halfWindow).toISOString(),
    new Date(Math.max(...validTimes) + halfWindow).toISOString(),
  ];
};

export const isStacItemLookupWindowValidForTime = (
  [startDate, endDate]: STACItemLookupWindow,
  timeValue: string | undefined,
  requiredMarginMs: number,
): boolean => {
  if (!startDate || !endDate || !timeValue) {
    return false;
  }

  const startTime = new Date(startDate).getTime();
  const endTime = new Date(endDate).getTime();
  const targetTime = new Date(timeValue).getTime();

  if (
    !Number.isFinite(startTime) ||
    !Number.isFinite(endTime) ||
    !Number.isFinite(targetTime)
  ) {
    return false;
  }

  return (
    targetTime - requiredMarginMs >= startTime &&
    targetTime + requiredMarginMs <= endTime
  );
};

const getSortedUniqueTimes = (timeValues: number[]): number[] =>
  Array.from(
    new Set(timeValues.filter((timeValue) => Number.isFinite(timeValue))),
  ).sort((firstTime, secondTime) => firstTime - secondTime);

const getClosestTimeIndex = (
  timeValues: { time: number }[],
  targetTime: number,
): number => {
  if (timeValues.length === 0) {
    throw new Error('getClosestTimeIndex requires at least one time value');
  }

  return timeValues.reduce((closestIndex, item, index) => {
    const closestDiff = Math.abs(timeValues[closestIndex].time - targetTime);
    const diff = Math.abs(item.time - targetTime);
    return diff < closestDiff ? index : closestIndex;
  }, 0);
};

export const getFrameStepInMilliseconds = (
  timeValue: string | undefined,
  prefetchList: number[],
  fallbackStepMs: number | undefined,
): number | undefined => {
  const targetTime = timeValue ? new Date(timeValue).getTime() : NaN;
  const prefetchTimes = getSortedUniqueTimes(prefetchList);

  if (Number.isFinite(targetTime) && prefetchTimes.length > 1) {
    const closestIndex = getClosestTimeIndex(
      prefetchTimes.map((time) => ({ time })),
      targetTime,
    );
    const frameSteps = [
      prefetchTimes[closestIndex] - prefetchTimes[closestIndex - 1],
      prefetchTimes[closestIndex + 1] - prefetchTimes[closestIndex],
    ].filter((step) => Number.isFinite(step) && step > 0);

    if (frameSteps.length > 0) {
      return Math.min(...frameSteps);
    }
  }

  return fallbackStepMs;
};

export const getLookupStepInMilliseconds = (
  frameStepMs: number | undefined,
  timeIntervalMs: number | undefined,
): number | undefined => {
  const candidateSteps = [frameStepMs, timeIntervalMs].filter(
    (step): step is number => step !== undefined && step > 0,
  );

  return candidateSteps.length > 0 ? Math.max(...candidateSteps) : undefined;
};

export const shouldPreloadStacFrames = (
  frameStepMs: number | undefined,
  timeIntervalMs: number | undefined,
): boolean =>
  frameStepMs === undefined ||
  timeIntervalMs === undefined ||
  frameStepMs >= timeIntervalMs;

export const getStacFrameTargetTimes = (
  timeValue: string | undefined,
  prefetchList: number[],
  fallbackStepMs: number | undefined,
  preloadRadius = STAC_GEOTIFF_FRAME_PRELOAD_RADIUS,
): number[] => {
  const targetTime = timeValue ? new Date(timeValue).getTime() : NaN;

  if (!Number.isFinite(targetTime)) {
    return [];
  }

  if (preloadRadius <= 0) {
    return [targetTime];
  }

  const prefetchTimes = getSortedUniqueTimes(prefetchList);

  if (prefetchTimes.length > 0) {
    const closestIndex = getClosestTimeIndex(
      prefetchTimes.map((time) => ({ time })),
      targetTime,
    );
    const startIndex = Math.max(0, closestIndex - preloadRadius);
    const endIndex = Math.min(
      prefetchTimes.length - 1,
      closestIndex + preloadRadius,
    );

    return getSortedUniqueTimes([
      ...prefetchTimes.slice(startIndex, endIndex + 1),
      targetTime,
    ]);
  }

  if (fallbackStepMs && fallbackStepMs > 0) {
    return getSortedUniqueTimes(
      Array.from(
        { length: preloadRadius * 2 + 1 },
        (_, index) => targetTime + (index - preloadRadius) * fallbackStepMs,
      ),
    );
  }

  return [targetTime];
};

export const getRequiredLookupMarginInMilliseconds = (
  timeValue: string | undefined,
  frameTargetTimes: number[],
  fallbackMarginMs: number,
): number => {
  const targetTime = timeValue ? new Date(timeValue).getTime() : NaN;

  if (!Number.isFinite(targetTime) || frameTargetTimes.length === 0) {
    return fallbackMarginMs;
  }

  const requiredMarginMs = Math.max(
    ...frameTargetTimes.map((frameTargetTime) =>
      Math.abs(frameTargetTime - targetTime),
    ),
  );

  return requiredMarginMs > 0 ? requiredMarginMs : fallbackMarginMs;
};

export const getStacFrameItemsToRender = (
  items: STACItem[] | undefined,
  timeValue?: string,
  frameTargetTimes?: number[],
): STACFrameItem[] => {
  if (!items || !timeValue) {
    return [];
  }

  const target = new Date(timeValue).getTime();
  if (!Number.isFinite(target)) {
    return [];
  }

  const itemsByTime = items
    .reduce<STACItemWithTime[]>((validItems, item, index) => {
      const { datetime } = item.properties;

      if (!datetime) {
        return validItems;
      }

      const time = new Date(datetime).getTime();

      if (Number.isFinite(time)) {
        validItems.push({ item, index, time });
      }

      return validItems;
    }, [])
    .sort(
      (firstItem, secondItem) =>
        firstItem.time - secondItem.time || firstItem.index - secondItem.index,
    );

  if (itemsByTime.length === 0) {
    return [];
  }

  const selectedItemIndex = getClosestTimeIndex(itemsByTime, target);
  const targetTimes =
    frameTargetTimes && frameTargetTimes.length > 0
      ? frameTargetTimes
      : [target];
  const itemIndexesToRender = Array.from(
    new Set([
      selectedItemIndex,
      ...targetTimes.map((targetTime) =>
        getClosestTimeIndex(itemsByTime, targetTime),
      ),
    ]),
  ).sort(
    (firstIndex, secondIndex) =>
      itemsByTime[firstIndex].time - itemsByTime[secondIndex].time ||
      itemsByTime[firstIndex].index - itemsByTime[secondIndex].index,
  );

  return itemIndexesToRender.map((itemIndex) => ({
    item: itemsByTime[itemIndex].item,
    isSelected: itemIndex === selectedItemIndex,
  }));
};
